import { supabase } from '../../lib/supabase';
import type { ApprovalRequest, ApprovalStep, ApprovalStepMode, QuoteActivity, QuoteComment, QuoteItemRow, QuoteRow, QuoteVersion, UserRole } from '../../types/domain';

export type ApprovalTask = { step: ApprovalStep; request: ApprovalRequest; quote: QuoteRow; items: QuoteItemRow[] };
export type ApprovalOverview = { request: ApprovalRequest; quote: QuoteRow; items: QuoteItemRow[]; steps: ApprovalStep[] };
export type Workflow = { id: string; name: string; description: string; active: boolean; is_default: boolean; created_by: string | null; created_at: string; updated_at: string };
export type WorkflowStep = { id: string; workflow_id: string; step_order: number; name: string; mode: ApprovalStepMode; approver_id: string; sort_order: number; active: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function versionQuote(snapshot: unknown, fallback: QuoteRow): QuoteRow {
  return isRecord(snapshot) && isRecord(snapshot.quote) ? { ...fallback, ...snapshot.quote } as QuoteRow : fallback;
}

function versionItems(snapshot: unknown): QuoteItemRow[] {
  if (!isRecord(snapshot) || !Array.isArray(snapshot.items)) return [];
  return snapshot.items.flatMap((value) => {
    if (!isRecord(value)) return [];
    const parameters = isRecord(value.parameters_snapshot) ? value.parameters_snapshot : {};
    const scenario = isRecord(parameters._scenario) ? parameters._scenario : {};
    const offering = isRecord(parameters._offering) ? parameters._offering : {};
    const subitems = Array.isArray(value.subitems) ? value.subitems.filter(isRecord) : [];
    return [{
      ...value,
      scenario_id_snapshot: typeof scenario.id === 'string' ? scenario.id : null,
      scenario_title_snapshot: typeof scenario.title === 'string' ? scenario.title : 'Тип работ',
      scenario_section_snapshot: typeof scenario.section === 'string' ? scenario.section : '',
      offering_id_snapshot: typeof offering.id === 'string' ? offering.id : null,
      offering_name_snapshot: typeof offering.name === 'string' ? offering.name : 'Услуга',
      price_calculation_mode_snapshot: value.price_calculation_mode_snapshot ?? 'own_price',
      subitems,
    } as unknown as QuoteItemRow];
  });
}

export async function listApprovalTasks(userId: string, includeAll = false): Promise<ApprovalTask[]> {
  const baseQuery = supabase.from('quote_approval_steps').select('*');
  const scopedQuery = includeAll ? baseQuery : baseQuery.eq('approver_id', userId);
  const { data: steps, error } = await scopedQuery.order('created_at', { ascending: false });
  if (error) throw error;
  const requestIds = [...new Set(steps.map((step) => step.request_id))];
  if (!requestIds.length) return [];
  const { data: requests, error: requestError } = await supabase.from('quote_approval_requests').select('*').in('id', requestIds);
  if (requestError) throw requestError;
  const quoteIds = [...new Set(requests.map((request) => request.quote_id))];
  const { data: quotes, error: quoteError } = await supabase.from('quotes').select('*').in('id', quoteIds);
  if (quoteError) throw quoteError;
  const versionIds = [...new Set(requests.map((request) => request.quote_version_id))];
  const { data: versions, error: versionError } = await supabase.from('quote_versions').select('id, snapshot').in('id', versionIds);
  if (versionError) throw versionError;
  const requestById = new Map(requests.map((request) => [request.id, request as ApprovalRequest]));
  const quoteById = new Map(quotes.map((quote) => [quote.id, quote as QuoteRow]));
  const versionById = new Map(versions.map((version) => [version.id, version.snapshot]));
  return steps.flatMap((step) => {
    const request = requestById.get(step.request_id);
    const quote = request ? quoteById.get(request.quote_id) : undefined;
    const snapshot = request ? versionById.get(request.quote_version_id) : undefined;
    return request && quote ? [{ step: step as ApprovalStep, request, quote: versionQuote(snapshot, quote), items: versionItems(snapshot) }] : [];
  });
}

export async function listApprovalOverviews(): Promise<ApprovalOverview[]> {
  const { data: requests, error } = await supabase
    .from('quote_approval_requests')
    .select('*')
    .order('submitted_at', { ascending: false });
  if (error) throw error;
  if (!requests.length) return [];

  const requestIds = requests.map((request) => request.id);
  const quoteIds = [...new Set(requests.map((request) => request.quote_id))];
  const versionIds = [...new Set(requests.map((request) => request.quote_version_id))];
  const [stepsResult, quotesResult, versionsResult] = await Promise.all([
    supabase.from('quote_approval_steps').select('*').in('request_id', requestIds).order('step_order').order('sort_order'),
    supabase.from('quotes').select('*').in('id', quoteIds),
    supabase.from('quote_versions').select('id, snapshot').in('id', versionIds),
  ]);
  if (stepsResult.error) throw stepsResult.error;
  if (quotesResult.error) throw quotesResult.error;
  if (versionsResult.error) throw versionsResult.error;

  const quoteById = new Map(quotesResult.data.map((quote) => [quote.id, quote as QuoteRow]));
  const versionById = new Map(versionsResult.data.map((version) => [version.id, version.snapshot]));
  const stepsByRequest = new Map<string, ApprovalStep[]>();
  for (const row of stepsResult.data) {
    const steps = stepsByRequest.get(row.request_id) ?? [];
    steps.push(row as ApprovalStep);
    stepsByRequest.set(row.request_id, steps);
  }

  return requests.flatMap((request) => {
    const quote = quoteById.get(request.quote_id);
    if (!quote) return [];
    const snapshot = versionById.get(request.quote_version_id);
    return [{
      request: request as ApprovalRequest,
      quote: versionQuote(snapshot, quote),
      items: versionItems(snapshot),
      steps: stepsByRequest.get(request.id) ?? [],
    }];
  });
}

export async function getQuoteApproval(quoteId: string) {
  const { data: requests, error } = await supabase.from('quote_approval_requests').select('*').eq('quote_id', quoteId).order('submitted_at', { ascending: false });
  if (error) throw error;
  const requestIds = requests.map((request) => request.id);
  const [stepsResult, versionsResult, activityResult] = await Promise.all([
    requestIds.length ? supabase.from('quote_approval_steps').select('*').in('request_id', requestIds).order('step_order').order('sort_order') : Promise.resolve({ data: [], error: null }),
    supabase.from('quote_versions').select('*').eq('quote_id', quoteId).order('version_number', { ascending: false }),
    supabase.from('quote_activity_log').select('*').eq('quote_id', quoteId).order('created_at', { ascending: false }),
  ]);
  if (stepsResult.error) throw stepsResult.error;
  if (versionsResult.error) throw versionsResult.error;
  if (activityResult.error) throw activityResult.error;
  return { requests: requests as ApprovalRequest[], steps: stepsResult.data as ApprovalStep[], versions: versionsResult.data as QuoteVersion[], activity: activityResult.data as QuoteActivity[] };
}

export async function listQuoteComments(quoteId: string): Promise<QuoteComment[]> {
  const { data, error } = await supabase
    .from('quote_comments')
    .select('*')
    .eq('quote_id', quoteId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data as QuoteComment[];
}

export async function addQuoteComment(quoteId: string, body: string): Promise<QuoteComment> {
  const { data, error } = await supabase.rpc('add_quote_comment', {
    p_quote_id: quoteId,
    p_body: body,
  });
  if (error) throw error;
  return data as QuoteComment;
}

export async function updateQuoteComment(commentId: string, body: string): Promise<QuoteComment> {
  const { data, error } = await supabase.rpc('update_quote_comment', {
    p_comment_id: commentId,
    p_body: body,
  });
  if (error) throw error;
  return data as QuoteComment;
}

export async function deleteQuoteComment(commentId: string): Promise<string> {
  const { data, error } = await supabase.rpc('delete_quote_comment', {
    p_comment_id: commentId,
  });
  if (error) throw error;
  return data as string;
}

export async function setQuoteCommentCompleted(commentId: string, completed: boolean): Promise<QuoteComment> {
  const { data, error } = await supabase.rpc('set_quote_comment_completed', {
    p_comment_id: commentId,
    p_completed: completed,
  });
  if (error) throw error;
  return data as QuoteComment;
}

export async function submitForApproval(quoteId: string, workflowId?: string | null) {
  const { data, error } = await supabase.rpc('submit_quote_for_approval', { p_quote_id: quoteId, p_workflow_id: workflowId ?? null });
  if (error) throw error;
  return data as ApprovalRequest;
}

export async function decideApproval(stepId: string, decision: 'approved' | 'rejected', comment = '') {
  const { data, error } = await supabase.rpc('decide_quote_approval', { p_step_id: stepId, p_decision: decision, p_comment: comment });
  if (error) throw error;
  return data as ApprovalRequest;
}

export async function withdrawApproval(stepId: string) {
  const { data, error } = await supabase.rpc('withdraw_quote_approval', { p_step_id: stepId });
  if (error) throw error;
  return data as ApprovalRequest;
}

export async function cancelApproval(requestId: string, comment = '') {
  const { data, error } = await supabase.rpc('cancel_quote_approval', { p_request_id: requestId, p_comment: comment });
  if (error) throw error;
  return data as ApprovalRequest;
}

export async function returnForRevision(requestId: string, comment: string) {
  const { data, error } = await supabase.rpc('return_quote_for_revision', { p_request_id: requestId, p_comment: comment });
  if (error) throw error;
  return data as ApprovalRequest;
}

export async function pendingApprovalCount(userId: string, role: UserRole | null) {
  if (!userId || !role || role === 'manager') return 0;
  const query = role === 'approver'
    ? supabase.from('quote_approval_steps').select('id', { count: 'exact', head: true }).eq('approver_id', userId).eq('status', 'pending')
    : supabase.from('quote_approval_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending');
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export async function markApprovalNotificationsRead(userId: string) {
  const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('user_id', userId).is('read_at', null);
  if (error) throw error;
}

export async function listWorkflows(): Promise<{ workflows: Workflow[]; steps: WorkflowStep[] }> {
  const [workflowResult, stepResult] = await Promise.all([
    supabase.from('approval_workflows').select('*').order('created_at'),
    supabase.from('approval_workflow_steps').select('*').order('step_order').order('sort_order'),
  ]);
  if (workflowResult.error) throw workflowResult.error;
  if (stepResult.error) throw stepResult.error;
  return { workflows: workflowResult.data as Workflow[], steps: stepResult.data as WorkflowStep[] };
}

export async function listApprovalProfiles() {
  const { data, error } = await supabase.from('profiles').select('id, full_name, position, role').order('full_name');
  if (error) throw error;
  return data;
}

export async function configureApprover(profileId: string, position: string, role: UserRole) {
  const { error } = await supabase.rpc('configure_profile', { p_profile_id: profileId, p_role: role, p_position: position });
  if (error) throw error;
}

export async function saveWorkflow(input: { id?: string; name: string; description: string; isDefault: boolean; participants: Array<{ approverId: string; name: string; order: number; mode: ApprovalStepMode }> }, userId: string) {
  if (input.isDefault) await supabase.from('approval_workflows').update({ is_default: false }).eq('is_default', true);
  const payload = { name: input.name, description: input.description, is_default: input.isDefault, active: true, created_by: userId };
  const result = input.id
    ? await supabase.from('approval_workflows').update(payload).eq('id', input.id).select('*').single()
    : await supabase.from('approval_workflows').insert(payload).select('*').single();
  if (result.error) throw result.error;
  const workflowId = result.data.id;
  const { error: deleteError } = await supabase.from('approval_workflow_steps').delete().eq('workflow_id', workflowId);
  if (deleteError) throw deleteError;
  const { error: stepError } = await supabase.from('approval_workflow_steps').insert(input.participants.map((participant, index) => ({
    workflow_id: workflowId,
    step_order: participant.order,
    sort_order: index,
    name: participant.name,
    mode: participant.mode,
    approver_id: participant.approverId,
    active: true,
  })));
  if (stepError) throw stepError;
  return result.data;
}
